import { Palette, SettingColour } from 'Shared/model/venueSettings';


/*
	The venue's 'dimBackground' - a filled surface a shade off the page (menu cards, the sub-nav
	button, the slideshow backdrop). It is an optional palette entry now, so a venue may have none;
	callers that read it by name use this to fall back to 'background' rather than painting nothing.
*/
export function dimBackground(palette:Palette): string
{
	return palette.dimBackground ?? palette.background;
}


/*
	The fill a nav button takes under the pointer: a sixth of white stirred into the surface it sits
	on when that surface is dark, a sixth of black when it is light. One default that reads on every
	venue's palette rather than only on the dark ones.

	Worked out in css rather than here because the surface is a palette entry the venue edits under us.
	'hsl(from X ...)' is relative colour syntax - it pulls the channels out of whatever X resolves to -
	and it is used only to pick which way to go: sign() is +1 below mid lightness and -1 above it, so
	the lightness lands on 100 (white) or 0 (black) and the hue and saturation are thrown away.

	Mixing towards white or black rather than shifting the surface's own hsl lightness, which is what
	the venue-set brightnessAdjustment in resolveColour() does. A near-white with a tint - The
	Drummer's '#fffef8' - carries a saturation of 100%, so dropping its lightness by any useful amount
	turns it a vivid yellow. Mixing keeps the hue where the venue put it.

	A surface at exactly 50% lightness gets sign() == 0, so it mixes towards mid grey and barely moves.
	Nothing to be done about that short of picking a side, and no venue's palette is anywhere near it.

	Venues override this by giving the node's 'Button (hover)' part a background of their own - a rule
	on the nav wrapper, which outranks the button's own class. See settingsParts in ruleProperties.ts.
*/
export function hoverBackground(surface:string): string
{
	const towards = `hsl(from ${surface} h 0 calc(50 + 50 * sign(50 - l)))`;
	return `color-mix(in srgb, ${surface} 85%, ${towards})`;
}


/*
	Turns a stored colour setting into css.

	Returns '' when the setting contributes nothing - either because the field is absent, or because
	it points at a palette entry the venue left empty or has since removed. Callers should use
	colourRule() rather than testing for that themselves.
*/
export function resolveColour(colour:SettingColour|string|undefined,palette:Palette): string
{
	if (colour==undefined)
		return '';

	/* A colour stored before palette links existed is a bare css string - treat it as a literal.
	   The style-params colour pickers wrote plain hex until seedCustomStyleColours normalised them. */
	if (typeof colour=='string')
		return colour;

	if (!('palette' in colour))
		return colour.literal;

	const base = palette[colour.palette] ?? '';
	const brightnessAdjustment = colour.brightnessAdjustment ?? 0;
	if (base=='' || brightnessAdjustment==0)
		return base;

	/*
		'hsl(from X ...)' is css relative colour syntax: it pulls the h/s/l channels out of whatever
		X resolves to and lets us shift them, so a shifted colour stays tied to its palette entry
		instead of being a second value somebody has to remember to change. A negative adjustment
		darkens: emit 'calc(s - 20)' rather than 'calc(s + -20)', which css calc() rejects.
	*/
	const operator = brightnessAdjustment > 0 ? '+' : '-';
	const amount = Math.abs(brightnessAdjustment);
	return `hsl(from ${base} h calc(s ${operator} ${amount}) calc(l ${operator} ${amount}))`;
}


/*
	The property/value pair for a colour setting, or {} when it contributes nothing - so that
	spreading it into a rule set leaves the rule untouched, exactly as an unset colour did back when
	the venue settings classes simply didn't mention it.
*/
export function colourRule(property:string,colour:SettingColour|string|undefined,palette:Palette): object
{
	const resolved = resolveColour(colour,palette);
	return resolved=='' ? {} : {[property]: resolved};
}


/*
	A border settings object with each side's colour resolved to a plain css colour.

	addBorders() interpolates the colour straight into a 'style colour width' shorthand and has no
	palette to resolve against, so a border that names a palette entry has to arrive already resolved.
	A side whose colour resolves to nothing - an entry the venue has since removed - keeps the value
	it had, which addBorders will render as it always did rather than dropping the border.

	The shape is the one the Sides control writes: {sides:'all'|'opposites'|'different'} plus a
	{style,colour,width} object per side named by that choice.
*/
export function resolvedBorder(border:object|undefined,palette:Palette): object|undefined
{
	if (border == undefined)
		return border;

	const sides = Object.fromEntries(Object.entries(border).map(([key,side]) =>
		key=='sides' || side==undefined || typeof side!='object' ? [key,side]
			: [key, {...side, colour: resolveColour((side as any).colour,palette) || (side as any).colour}]
	));

	return sides;
}
